import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { createEvent, formatSlug, validateSlugFormat, checkSlugAvailability } from '../services/eventsService';
import { getAppBaseUrl } from '../utils/sharingLinkUrl';

const { FiUsers, FiSave } = FiIcons;

const CreateEvent = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const slugCheckTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    event_slug: '',
    description: '',
    type: 'seminar',
    capacity: '',
  });

  const [errors, setErrors] = useState({});
  const [slugTaken, setSlugTaken] = useState(false);

  useEffect(() => {
    return () => {
      if (slugCheckTimeoutRef.current) clearTimeout(slugCheckTimeoutRef.current);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'title' && !slugTouched) {
        next.event_slug = formatSlug(value);
      }
      return next;
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    if (name === 'event_slug') {
      setSlugTouched(true);
      setSlugTaken(false);
      if (slugCheckTimeoutRef.current) clearTimeout(slugCheckTimeoutRef.current);
      const valueToCheck = value;
      slugCheckTimeoutRef.current = setTimeout(() => {
        const slug = formatSlug(valueToCheck) || String(valueToCheck).trim();
        if (slug) {
          checkSlugAvailability(slug).then(({ data }) => {
            if (data) setSlugTaken(!data.available);
          });
        } else {
          setSlugTaken(false);
        }
      }, 400);
    }
  };

  const handleSlugBlur = () => {
    const slug = formatSlug(formData.event_slug) || formData.event_slug.trim();
    if (!slug) return;
    checkSlugAvailability(slug).then(({ data }) => {
      if (data) setSlugTaken(!data.available);
    });
  };

  const suggestSlugFromTitle = () => {
    const suggested = formatSlug(formData.title);
    setFormData(prev => ({ ...prev, event_slug: suggested }));
    setSlugTouched(false);
    setSlugTaken(false);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Event title is required';
    }
    const slug = formatSlug(formData.event_slug) || formData.event_slug.trim();
    if (!slug) {
      newErrors.event_slug = 'Event slug is required';
    } else {
      const formatCheck = validateSlugFormat(slug);
      if (!formatCheck.valid) {
        newErrors.event_slug = formatCheck.message;
      }
    }
    if (slugTaken) {
      newErrors.event_slug = 'This slug is already in use. Please choose another.';
    }
    if (!formData.type) {
      newErrors.type = 'Event type is required';
    }
    if (formData.capacity && (parseInt(formData.capacity) <= 0 || isNaN(parseInt(formData.capacity)))) {
      newErrors.capacity = 'Capacity must be a positive number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profile?.id) {
      toast.error('You must be logged in to create an event');
      return;
    }
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    setIsSubmitting(true);
    try {
      const slug = formatSlug(formData.event_slug) || formData.event_slug.trim();
      const eventData = {
        agent_id: profile.id,
        title: formData.title.trim(),
        event_slug: slug,
        description: formData.description.trim() || null,
        type: formData.type,
        status: 'draft',
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
      };
      const { data: event, error: eventError } = await createEvent(eventData, {
        userId: profile.id,
        role: profile.role,
      });
      if (eventError) {
        if (eventError.code === 'duplicate_slug') {
          setErrors(prev => ({ ...prev, event_slug: eventError.message }));
        }
        throw eventError;
      }
      toast.success('Event created! Add sessions and details in the editor.');
      navigate(`/dashboard/events/${event.id}/edit`);
    } catch (error) {
      toast.error(error?.message || 'Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Event</h1>
          <p className="text-gray-600 mt-1">Set up basic event information. You'll add sessions and details in the editor.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Event Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter event title"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="event_slug" className="block text-sm font-medium text-gray-700 mb-1">
                    Event Slug *
                  </label>
                  <input
                    type="text"
                    id="event_slug"
                    name="event_slug"
                    value={formData.event_slug}
                    onChange={handleChange}
                    onBlur={handleSlugBlur}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent lowercase"
                    placeholder="my-event-slug"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This will be part of your event URL. Use lowercase letters, numbers, and hyphens only.
                  </p>
                  {formData.title && !slugTouched && (
                    <p className="text-xs text-blue-600 mt-1">
                      <button type="button" onClick={suggestSlugFromTitle} className="underline hover:no-underline">
                        Use suggested: {formatSlug(formData.title) || '—'}
                      </button>
                    </p>
                  )}
                  {formData.event_slug && (
                    <p className="text-xs text-gray-600 mt-1 font-mono">
                      {getAppBaseUrl()}/events/{(formatSlug(formData.event_slug) || formData.event_slug.trim()) || '…'}
                    </p>
                  )}
                  {(errors.event_slug || slugTaken) && (
                    <p className="text-red-500 text-sm mt-1">{errors.event_slug || 'This slug is already in use.'}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe your event..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                      Event Type *
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="seminar">Seminar</option>
                      <option value="workshop">Workshop</option>
                      <option value="course">Course</option>
                    </select>
                    {errors.type && (
                      <p className="text-red-500 text-sm mt-1">{errors.type}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
                      Max Attendees
                    </label>
                    <div className="relative">
                      <SafeIcon icon={FiUsers} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="number"
                        id="capacity"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleChange}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Unlimited"
                        min="1"
                      />
                    </div>
                    {errors.capacity && (
                      <p className="text-red-500 text-sm mt-1">{errors.capacity}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Event Summary</h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium text-gray-900 capitalize">{formData.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Capacity:</span>
                  <span className="font-medium text-gray-900">{formData.capacity || 'Unlimited'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                    Draft
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-4 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                    isSubmitting
                      ? 'bg-blue-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>{isSubmitting ? 'Creating Event...' : 'Create Event'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/dashboard/events')}
                  className="w-full border border-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateEvent;
